import { z } from "zod";
import { FINANCE_EVENT_NAMES, type FinanceEventName } from "@/lib/finance-instrumentation/registry";

const isoDateTime = z.string().datetime({ offset: true });
const environmentEnum = z.enum(["prod", "staging", "dev"]);
const sourceSystemEnum = z.enum(["web_app", "api", "worker", "billing_service", "edge_function"]);
const paymentMethodEnum = z.enum(["card", "bank_transfer", "wallet", "other"]);
const refundTypeEnum = z.enum(["full", "partial"]);
const destinationTypeEnum = z.enum(["bank_account", "card", "wallet"]);
const invoiceStatusEnum = z.enum(["draft", "open", "paid", "void", "overdue"]);

export const financeBaseEventSchema = z.object({
  event_id: z.string().guid(),
  event_name: z.enum(FINANCE_EVENT_NAMES),
  event_timestamp: isoDateTime,
  received_at: isoDateTime,
  schema_version: z.string().min(1),
  user_id: z.string().min(1).nullable(),
  account_id: z.string().min(1),
  session_id: z.string().min(1).nullable(),
  request_id: z.string().min(1).nullable(),
  idempotency_key: z.string().min(1).nullable(),
  source_system: sourceSystemEnum,
  environment: environmentEnum,
  currency: z.string().length(3),
  amount_minor: z.number().int().nullable(),
  country_code: z.string().length(2).nullable(),
  is_backfill: z.boolean().optional().default(false),
});

export const invoiceEventSchema = financeBaseEventSchema.extend({
  invoice_id: z.string().min(1),
  invoice_number: z.string().min(1),
  invoice_due_at: isoDateTime,
  invoice_status: invoiceStatusEnum,
  line_item_count: z.number().int().nonnegative(),
  customer_id: z.string().min(1),
});

export const paymentEventSchema = financeBaseEventSchema.extend({
  payment_intent_id: z.string().min(1),
  payment_id: z.string().min(1).nullable(),
  payment_method_type: paymentMethodEnum,
  processor: z.string().min(1),
  processor_response_code: z.string().min(1).nullable(),
  failure_reason: z.string().min(1).nullable(),
  is_retry: z.boolean(),
  retry_attempt: z.number().int().nonnegative(),
});

export const refundEventSchema = financeBaseEventSchema.extend({
  refund_id: z.string().min(1),
  payment_id: z.string().min(1),
  refund_reason: z.string().min(1),
  refund_type: refundTypeEnum,
  refund_amount_minor: z.number().int().nonnegative(),
});

export const payoutEventSchema = financeBaseEventSchema.extend({
  payout_id: z.string().min(1),
  payout_batch_id: z.string().min(1).nullable(),
  destination_type: destinationTypeEnum,
  settlement_eta: isoDateTime.nullable(),
  net_amount_minor: z.number().int(),
  fee_amount_minor: z.number().int().nonnegative(),
});

export type FinanceBaseEvent = z.infer<typeof financeBaseEventSchema>;
export type InvoiceFinanceEvent = z.infer<typeof invoiceEventSchema>;
export type PaymentFinanceEvent = z.infer<typeof paymentEventSchema>;
export type RefundFinanceEvent = z.infer<typeof refundEventSchema>;
export type PayoutFinanceEvent = z.infer<typeof payoutEventSchema>;

export function schemaForEventName(eventName: FinanceEventName) {
  if (eventName.startsWith("finance.invoice.")) return invoiceEventSchema;
  if (eventName.startsWith("finance.payment_intent.") || eventName.startsWith("finance.payment.")) {
    return paymentEventSchema;
  }
  if (eventName.startsWith("finance.refund.")) return refundEventSchema;
  if (eventName.startsWith("finance.payout.")) return payoutEventSchema;
  return financeBaseEventSchema;
}

